import React from 'react';
import styled, { keyframes, css } from 'styled-components';

// --- ANIMATIONS ---
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// --- COLOR PALETTE & CONSTANTS ---
const colors = {
  primary: '#2c3e50',
  secondary: '#34495e',
  accent: '#FFC107',
  accentDark: '#E6AC00',
  success: '#28a745',
  info: '#17a2b8',
  backgroundLight: '#f9f9f9',
  backgroundWhite: '#ffffff',
  textBody: '#555',
  textHeader: '#333',
  shadowLight: 'rgba(0, 0, 0, 0.05)',
  shadowMedium: 'rgba(0, 0, 0, 0.1)',
  shadowHeavy: 'rgba(0, 0, 0, 0.15)',
};

const breakpoints = {
  mobile: '480px',
  tablet: '768px',
  desktop: '992px',
  large: '1200px',
};

// --- MIXINS ---
const primaryButtonStyles = css`
  background: linear-gradient(135deg, ${colors.accent} 0%, #FFD700 100%);
  color: ${colors.primary};
  padding: 16px 40px;
  border-radius: 12px;
  text-decoration: none;
  font-size: clamp(1em, 2vw, 1.1em);
  font-weight: 700;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: inline-block;
  border: none;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(255, 193, 7, 0.4);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.6s;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 35px rgba(255, 193, 7, 0.5);
    background: linear-gradient(135deg, ${colors.accentDark} 0%, ${colors.accent} 100%);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 14px 30px;
  }
`;

const cardHoverEffect = css`
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &:hover {
    transform: translateY(-12px);
    box-shadow: 0 25px 50px ${colors.shadowHeavy};
  }
`;

// --- STYLED COMPONENTS ---

const PageContainer = styled.div`
  font-family: 'Poppins', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: ${colors.textHeader};
  padding: 60px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, ${colors.backgroundWhite} 50%, #f5f7fa 100%);
  line-height: 1.6;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.2em, 5vw, 3.2em);
  color: ${colors.primary};
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  padding-bottom: 25px;
  font-weight: 800;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  &::after {
    content: '';
    display: block;
    width: 120px;
    height: 6px;
    background: linear-gradient(90deg, ${colors.accent}, ${colors.accentDark});
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 0;
    border-radius: 4px;
    animation: ${pulse} 2s infinite;
  }

  @media (max-width: ${breakpoints.tablet}) {
    margin-bottom: 50px;
    padding-bottom: 20px;
    
    &::after {
      width: 100px;
      height: 5px;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 50px;
  padding: 0 5%;
  margin-bottom: 100px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: ${breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
    gap: 60px;
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 0 4%;
    gap: 40px;
  }
`;

const GridItem = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.backgroundWhite};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 40px ${colors.shadowMedium};
  ${cardHoverEffect}
  border: 1px solid rgba(255, 193, 7, 0.1);
  position: relative;
  animation: ${fadeInUp} 0.8s ease-out ${props => props.delay || '0s'} both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.accent}, ${colors.accentDark});
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  &:nth-child(even) {
    flex-direction: row-reverse;
    animation: ${fadeInUp} 0.8s ease-out ${props => props.delay || '0s'} both;
  }

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column !important;
    border-radius: 16px;
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 320px;
  object-fit: cover;
  transition: transform 0.6s ease;
  filter: brightness(0.9);

  ${GridItem}:hover & {
    transform: scale(1.08);
    filter: brightness(1);
  }

  @media (max-width: ${breakpoints.tablet}) {
    width: 100%;
    height: 280px;
  }
`;

const ItemContent = styled.div`
  width: 100%;
  padding: 45px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  @media (max-width: ${breakpoints.tablet}) {
    width: 100%;
    padding: 35px 30px;
  }
`;

const ItemHeader = styled.h3`
  font-size: clamp(1.5em, 2.5vw, 1.9em);
  margin-top: 0;
  color: ${colors.primary};
  margin-bottom: 25px;
  font-weight: 700;
  line-height: 1.3;
  position: relative;
  padding-bottom: 15px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: linear-gradient(90deg, ${colors.accent}, transparent);
    border-radius: 2px;
  }

  ${GridItem}:nth-child(even) &::after {
    left: auto;
    right: 0;
  }

  @media (max-width: ${breakpoints.tablet}) {
    &::after {
      left: 50% !important;
      transform: translateX(-50%);
    }
  }
`;

const BenefitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 25px 0 0 0;
`;

const BenefitItem = styled.li`
  font-size: clamp(1em, 2vw, 1.1em);
  margin-bottom: 18px;
  position: relative;
  padding-left: 40px;
  color: ${colors.textBody};
  line-height: 1.6;
  transition: transform 0.3s ease;
  animation: ${slideInLeft} 0.6s ease-out ${props => props.delay || '0s'} both;

  &:hover {
    transform: translateX(5px);
    color: ${colors.primary};
  }

  &::before {
    content: '⚡';
    color: ${colors.accent};
    font-weight: bold;
    position: absolute;
    left: 0;
    top: 0;
    font-size: 1.3em;
    line-height: 1.4;
    background: rgba(255, 193, 7, 0.1);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
  }

  &:hover::before {
    background: rgba(255, 193, 7, 0.2);
    transform: scale(1.1);
  }
`;

const StyledParagraph = styled.p`
  font-size: clamp(1.05em, 2vw, 1.1em);
  line-height: 1.8;
  color:#000000;
  margin-bottom: 20px;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const CallToActionSection = styled.section`
  background: linear-gradient(135deg, ${colors.backgroundLight} 0%, #e9ecef 100%);
  padding: 80px 5%;
  text-align: center;
  margin-bottom: 100px;
  border-radius: 20px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0 20px 60px ${colors.shadowLight};
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, ${colors.accent}, ${colors.accentDark}, ${colors.accent});
    background-size: 200% 100%;
    animation: ${gradientShift} 3s ease infinite;
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 60px 4%;
    margin-bottom: 70px;
    border-radius: 16px;
  }
`;

const CtaText = styled.p`
  font-size: clamp(1.1em, 2.5vw, 1.3em);
  line-height: 1.9;
  max-width: 1000px;
  margin: 0 auto 50px auto;
  color: ${colors.primary};
  text-align: center;
  font-weight: 500;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const CtaButton = styled.a`
  ${primaryButtonStyles}
  animation: ${pulse} 2s infinite 1s, ${fadeInUp} 0.8s ease-out 0.4s both;
`;

const SmallText = styled.p`
  font-size: clamp(0.9em, 2vw, 1em);
  color: ${colors.secondary};
  margin-top: 35px;
  font-style: italic;
  font-weight: 400;
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;
`;

const RichContentSection = styled.section`
  padding: 0 5%;
  margin-bottom: 100px;
  display: flex;
  gap: 80px;
  align-items: center;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: ${breakpoints.desktop}) {
    flex-direction: column;
    gap: 50px;
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 0 4%;
    margin-bottom: 70px;
  }
`;

const RichTextColumn = styled.div`
  flex: 1.2;
  animation: ${slideInLeft} 0.8s ease-out;
`;

const RichImageColumn = styled.div`
  flex: 0.8;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${slideInRight} 0.8s ease-out;

  @media (max-width: ${breakpoints.desktop}) {
    order: -1;
    width: 100%;
  }
`;

const RichImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 20px 60px ${colors.shadowMedium};
  transition: all 0.5s ease;
  filter: brightness(0.95);

  &:hover {
    transform: scale(1.05) rotate(1deg);
    box-shadow: 0 25px 80px ${colors.shadowHeavy};
    filter: brightness(1);
  }
`;

const RichTextHeader = styled.h3`
  font-size: clamp(1.8em, 3vw, 2.4em);
  color: ${colors.primary};
  margin-bottom: 30px;
  font-weight: 800;
  line-height: 1.3;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ListWithBullets = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
`;

const BulletItem = styled.li`
  font-size: clamp(1.05em, 2vw, 1.1em);
  line-height: 1.9;
  margin-bottom: 22px;
  position: relative;
  padding-left: 35px;
  color: ${colors.textBody};
  animation: ${slideInLeft} 0.6s ease-out ${props => props.delay || '0s'} both;

  &::before {
    content: '🔍';
    color: ${colors.accent};
    font-size: 1.1em;
    position: absolute;
    left: 0;
    top: 0;
    line-height: 1.9;
    transition: all 0.3s ease;
    background: rgba(255, 193, 7, 0.1);
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover::before {
    transform: scale(1.2) rotate(10deg);
    background: rgba(255, 193, 7, 0.2);
  }
`;

const BottomCtaSection = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  color: white;
  padding: 80px 5%;
  text-align: center;
  margin-bottom: 100px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 0 30px rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, ${colors.accent}, ${colors.accentDark});
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: ${float} 6s ease-in-out infinite;
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 60px 4%;
    margin-bottom: 70px;
    border-radius: 16px;
  }
`;

const BottomCtaTitle = styled.h3`
  font-size: clamp(2em, 4vw, 2.8em);
  margin-top: 0;
  margin-bottom: 30px;
  color: white;
  font-weight: 800;
  letter-spacing: -0.5px;
  position: relative;
  z-index: 2;
`;

const BottomCtaButton = styled.a`
  ${primaryButtonStyles}
  background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%);
  color: ${colors.primary};
  position: relative;
  z-index: 2;

  &:hover {
    background: white;
    color: ${colors.primary};
    box-shadow: 0 15px 40px rgba(255, 255, 255, 0.4);
  }
`;

const SeoOptimizationSection = styled(RichContentSection)`
  @media (max-width: ${breakpoints.desktop}) {
    flex-direction: column;
  }
`;

const SeoTextColumn = styled(RichTextColumn)`
  flex: 1.5;
`;

const SeoImageColumn = styled(RichImageColumn)`
  flex: 1;

  @media (max-width: ${breakpoints.desktop}) {
    order: 0;
  }
`;

const SeoImage = styled(RichImage)`
  max-width: 500px;
`;

const SeoSubtitle = styled(RichTextHeader)`
  font-size: clamp(1.6em, 3vw, 2em);
`;

const ReadMoreButton = styled(CtaButton)`
  background: linear-gradient(135deg, ${colors.info} 0%, #138496 100%);
  color: white;
  margin-top: 35px;
  font-weight: 600;
  
  &:hover {
    background: linear-gradient(135deg, #138496 0%, #117a8b 100%);
    box-shadow: 0 12px 35px rgba(23, 162, 184, 0.5);
    transform: translateY(-4px);
    color: white;
  }
`;

// --- REACT COMPONENT ---

const CustomSEO2 = () => {
  return (
    <PageContainer>
      <SectionTitle>Why Choose SRK Software for the Best SEO Services?</SectionTitle>

      <ContentGrid>
        <GridItem delay="0.1s">
          <ItemImage 
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80" 
            alt="SEO Strategy Analytics Dashboard" 
          />
          <ItemContent>
            <ItemHeader>Result-Oriented SEO Strategies</ItemHeader>
            <StyledParagraph>
              SRK Software has a team of SEO experts who stay up-to-date with the latest trends and algorithms. We implement **data-driven strategies** for guaranteed, sustainable results and clear ROI.
            </StyledParagraph>
            <BenefitList>
              <BenefitItem delay="0.2s">Guaranteed Result-Oriented Strategies</BenefitItem>
              <BenefitItem delay="0.3s">Effective, Future-Proof Solutions</BenefitItem>
              <BenefitItem delay="0.4s">Proven Track Record of Success</BenefitItem>
              <BenefitItem delay="0.5s">Creative & Custom Solutions</BenefitItem>
            </BenefitList>
          </ItemContent>
        </GridItem>

        <GridItem delay="0.2s" style={{display:'flex', flexDirection:'column'}}>
          <ItemImage 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80" 
            alt="Diverse Industry Experience" 
          />
          <ItemContent>
            <ItemHeader>Diverse Industry Experience</ItemHeader>
            <StyledParagraph>
              Our team has worked with clients across various industries, giving us diverse insights that allow us to create flexible and effective SEO strategies tailored for **any niche market**.
            </StyledParagraph>
            <BenefitList>
              <BenefitItem delay="0.3s">Expertise in Various Domains</BenefitItem>
              <BenefitItem delay="0.4s">Customized, Adaptable Approaches</BenefitItem>
              <BenefitItem delay="0.5s">Proven Success Stories Across Industries</BenefitItem>
              <BenefitItem delay="0.6s">Client-Centric & Collaborative Solutions</BenefitItem>
            </BenefitList>
          </ItemContent>
        </GridItem>

        <GridItem delay="0.3s">
          <ItemImage 
            src="https://images.unsplash.com/photo-1535223289827-42f1e9919769?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
            alt="AI and Technology in SEO"
          />
          <ItemContent>
            <ItemHeader>Cutting-Edge Technology & AI</ItemHeader>
            <StyledParagraph>
              We embrace the latest SEO tools and advancements. We provide our clients with a competitive advantage through **advanced analytics**, AI-driven keyword research, and cutting-edge optimization techniques.
            </StyledParagraph>
            <BenefitList>
              <BenefitItem delay="0.4s">Advanced SEO Tool Stack Integration</BenefitItem>
              <BenefitItem delay="0.5s">Deep Data-Driven Insights</BenefitItem>
              <BenefitItem delay="0.6s">AI-Powered Keyword Research</BenefitItem>
              <BenefitItem delay="0.7s">Latest Algorithm Techniques</BenefitItem>
            </BenefitList>
          </ItemContent>
        </GridItem>
      </ContentGrid>

      <CallToActionSection>
        <CtaText>
          **Keywords** play a pivotal role in online presence. They drive organic traffic by helping search engines understand your content, thus improving your ranking and making you visible to potential clients. We conduct **thorough keyword research**, analyze competitor strategies, and implement data-driven methods to identify high-value keywords, ensuring your website ranks prominently and increases your return on investment.
        </CtaText>
        <CtaButton href="#contact">Start Your Custom Keyword Strategy</CtaButton>
        <SmallText>"With custom keyword solutions, you can gain access to valuable insights and save time."</SmallText>
      </CallToActionSection>

      <RichContentSection>
        <RichTextColumn>
          <RichTextHeader>On-Page SEO: The Foundation of Visibility</RichTextHeader>
          <StyledParagraph>
            On-page optimization focuses on elements *within* your website that directly impact visibility and user experience. This includes optimizing content, meta tags, and the internal linking structure to enhance search engine rankings. By making your website truly search engine-friendly, you attract organic traffic and drive conversions.
          </StyledParagraph>
          <ListWithBullets>
            <BulletItem delay="0.1s">
              **In-Depth On-Page Audits:** Enhancing website visibility through deep content optimization, keyword placement, meta tags, and internal linking structure.
            </BulletItem>
            <BulletItem delay="0.2s">
              **Technical SEO Audit & Speed Optimization:** Identifying and fixing website issues (speed, mobile-friendliness, structured data) that impact search engine crawling and indexing performance.
            </BulletItem>
            <BulletItem delay="0.3s">
              **Advanced Local SEO Strategy:** Improving your local search rankings via Google My Business optimization, local citation building, and geographic keyword targeting.
            </BulletItem>
          </ListWithBullets>
        </RichTextColumn>
        <RichImageColumn>
          <RichImage 
            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80" 
            alt="On-Page SEO Optimization Dashboard" 
          />
        </RichImageColumn>
      </RichContentSection>

      <BottomCtaSection>
        <BottomCtaTitle>Off-Page SEO: Building Trust and Authority</BottomCtaTitle>
        <StyledParagraph>
          Off-page SEO is about establishing your website as an authority through external validation. SRK Software builds **high-quality, relevant backlinks** from authoritative sources, conducts competitor backlink analysis, and focuses on ethical, sustainable link-building strategies to enhance your website's domain authority and search engine rankings securely.
        </StyledParagraph>
        <BottomCtaButton href="#learn-more">I Want To Generate More Leads ➔</BottomCtaButton>
      </BottomCtaSection>

      <SeoOptimizationSection>
        <SeoImageColumn>
          <SeoImage 
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80" 
            alt="Comprehensive SEO Analysis" 
          />
        </SeoImageColumn>
        <SeoTextColumn>
          <SeoSubtitle>Comprehensive Website SEO Optimization Services</SeoSubtitle>
          <StyledParagraph>
            Website SEO optimization is crucial for increasing your visibility. Our strategies focus on enhancing your site's appeal to search engines, which improves rankings and brings in more qualified visitors. We optimize every element of your website, from content to technical architecture, to ensure it meets the highest search engine standards.
          </StyledParagraph>
          <StyledParagraph>
            We integrate high-value, relevant keywords strategically throughout your site. Our techniques include optimizing meta titles, descriptions, headings, and image alt tags, ensuring every element is aligned with the latest SEO best practices and is designed to drive traffic and qualified leads.
          </StyledParagraph>
          <ReadMoreButton href="#read-more-seo">Discover Our Full SEO Process ➔</ReadMoreButton>
        </SeoTextColumn>
      </SeoOptimizationSection>

      <BottomCtaSection style={{
        background: `linear-gradient(135deg, ${colors.accent} 0%, #FFD700 100%)`, 
        marginBottom: '0px', 
        borderRadius: '20px 20px 0 0',
        padding: '100px 5%'
      }}>
        <BottomCtaTitle style={{color: colors.primary}}>Ready to Expand Your Brand in the World of SEO and Grow Your Business?</BottomCtaTitle>
        <StyledParagraph style={{color: colors.primary, marginBottom: '40px'}}>
          Join hundreds of successful businesses that have transformed their online presence with our proven SEO strategies.
        </StyledParagraph>
        <BottomCtaButton 
          href="#contact-us" 
          style={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            color: 'white',
            boxShadow: `0 12px 35px ${colors.shadowHeavy}`,
          }}
        >
          Get Started Now ➔
        </BottomCtaButton>
      </BottomCtaSection>
    </PageContainer>
  );
};

export default CustomSEO2;